import { create } from 'zustand';
import type { Meditation } from '@/types/meditation';

interface MeditationPlayerState {
  // Состояние трека
  currentMeditation: Meditation | null;
  isPlaying: boolean;
  currentTime: number;       // секунды
  duration: number;          // секунды
  historyId: string | null;  // id записи в meditation_history
  loop: boolean;             // циклическое воспроизведение
  
  // 🆕 Глобальный audio элемент
  audioElement: HTMLAudioElement | null;
  
  // 🆕 Признак "трек закрыт мини-плеером"
  isClosed: boolean;
  
  // Actions
  setCurrentMeditation: (m: Meditation | null) => void;
  setIsPlaying: (v: boolean) => void;
  setCurrentTime: (sec: number) => void;
  setDuration: (sec: number) => void;
  setHistoryId: (id: string | null) => void;
  setLoop: (v: boolean) => void;
  setAudioElement: (el: HTMLAudioElement | null) => void;
  setIsClosed: (v: boolean) => void;
  reset: () => void;
  
  // 🆕 Императивные методы воспроизведения
  play: () => Promise<void>;
  pause: () => void;
  seek: (seconds: number) => void;
  skipBack: (seconds: number) => void;
  skipForward: (seconds: number) => void;
  stop: () => void;
}

/**
 * Получаем audio элемент. Если в store нет — ищем напрямую в DOM.
 * Это защита от race condition при смене трека.
 */
function getAudio(stateAudio: HTMLAudioElement | null): HTMLAudioElement | null {
  if (stateAudio) return stateAudio;
  // Fallback — ищем по id в DOM
  const fromDom = document.getElementById('global-audio-player') as HTMLAudioElement | null;
  return fromDom;
}

export const useMeditationPlayerStore = create<MeditationPlayerState>((set, get) => ({
  currentMeditation: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  historyId: null,
  loop: false,
  audioElement: null,
  isClosed: false,
  
  setCurrentMeditation: (m) => set({ currentMeditation: m, isClosed: false }),
  setIsPlaying: (v) => set({ isPlaying: v }),
  setCurrentTime: (sec) => set({ currentTime: sec }),
  setDuration: (sec) => set({ duration: sec }),
  setHistoryId: (id) => set({ historyId: id }),
  setLoop: (v) => set({ loop: v }),
  setAudioElement: (el) => set({ audioElement: el }),
  setIsClosed: (v) => set({ isClosed: v }),
  
  reset: () => set({
    currentMeditation: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    historyId: null,
    loop: false,
    isClosed: false,
  }),
  
  // Императивные методы — управляют audio напрямую с fallback на DOM
  play: async () => {
    const audio = getAudio(get().audioElement);
    if (!audio) {
      console.warn('[audio store] no audio element');
      return;
    }
    try {
      await audio.play();
      set({ isPlaying: true });
    } catch (e) {
      console.error('[audio store] play error:', e);
      set({ isPlaying: false });
    }
  },
  
  pause: () => {
    const audio = getAudio(get().audioElement);
    if (!audio) {
      console.warn('[audio store] no audio element for pause');
      return;
    }
    audio.pause();
    set({ isPlaying: false });
  },
  
  seek: (seconds: number) => {
    const audio = getAudio(get().audioElement);
    if (!audio) {
      console.warn('[audio store] no audio element for seek');
      return;
    }
    const safeDuration = isFinite(audio.duration) ? audio.duration : seconds;
    audio.currentTime = Math.max(0, Math.min(seconds, safeDuration));
    set({ currentTime: audio.currentTime });
  },
  
  skipBack: (seconds: number) => {
    const audio = getAudio(get().audioElement);
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - seconds);
  },
  
  skipForward: (seconds: number) => {
    const audio = getAudio(get().audioElement);
    if (!audio) return;
    const safeDuration = isFinite(audio.duration) ? audio.duration : Infinity;
    audio.currentTime = Math.min(safeDuration, audio.currentTime + seconds);
  },
  
  stop: () => {
    const audio = getAudio(get().audioElement);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    set({
      currentMeditation: null,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      historyId: null,
      isClosed: true,
    });
  },
}));
